import type { SettingsTranslation } from "./index";

export const english = {
  section_appearance: "Appearance & language",
  section_notifications: "Notifications",
  section_privacy: "Privacy",
  section_security: "Security",
  saving: "Saving...",
  saved: "Saved",
  auto_analyze_recordings: "Auto analyze sessions",
  auto_analyze_recordings_help:
    "Analyze each session as soon as it uploads, which is right after you approve it when Review before upload is on. When off, sessions wait as Not analyzed until you start the analysis.",
  recording_screenshot_format: "Compression",
  recording_screenshot_format_help:
    "Compressing screenshots means less network traffic and faster sync, but uses CPU while recording. Turn it off to optimize for performance.",
  screenshot_format_load_failed: "Unable to load compression setting",
  screenshot_format_update_failed: "Unable to update compression setting",
  recording_screenshot_blacklist: "Excluded apps",
  recording_screenshot_blacklist_help:
    "Nothing is recorded while one of these apps is in the foreground.",
  screenshot_blacklist_load_failed: "Unable to load excluded apps",
  screenshot_blacklist_update_failed: "Unable to update excluded apps",
  screenshot_blacklist_app_placeholder: "App name",
  screenshot_blacklist_add: "Add",
  screenshot_blacklist_empty: "No apps are excluded.",
  screenshot_blacklist_remove: "Remove {{app}}",
  recording_screenshot_redaction: "Sensitive text redaction",
  recording_screenshot_redaction_help:
    "Black out sensitive text detected in screenshots (emails, card numbers, credentials…) before they are saved. Detection is best-effort; applies from the next recording start.",
  screenshot_redaction_load_failed: "Unable to load sensitive text redaction setting",
  redaction_categories: "What gets redacted",
  redaction_category_enabled: "Enabled",
  redaction_category_experimental: "Include experimental",
  redaction_experimental_help:
    "Experimental detections catch more sensitive text, but they are less precise and may also black out harmless content.",
  redaction_category_credentials: "Credentials & secrets",
  redaction_category_accounts: "Cards & bank accounts",
  redaction_category_financial: "Financial amounts",
  redaction_category_personal: "Personal identity",
  redaction_category_contact: "Contact details",
  redaction_category_network: "Network addresses",
  redaction_category_organizations: "Organizations",
  redaction_category_suspicious: "Suspicious-looking text",
  redaction_category_help: {
    credentials:
      "Passwords, API keys, access tokens, and other secrets that grant access to accounts and systems.",
    accounts: "Credit card numbers, IBANs, and other payment and bank account details.",
    financial: "Salaries, compensation, and other financial amounts.",
    personal: "Identity document numbers, tax codes, dates of birth, and people's names.",
    contact: "Email addresses, phone numbers, and postal addresses.",
    network: "IP addresses and other network identifiers.",
    organizations: "Names of companies and other organizations.",
    suspicious:
      "Text that doesn't fit a specific category but still looks sensitive, like long runs of digits.",
  },
  notification_frequency: "Email frequency",
  notification_frequency_help:
    "How often alert emails may be sent. Held-back results are bundled into the next one.",
  notification_frequency_immediately: "Immediately",
  notification_frequency_daily: "At most daily",
  notification_frequency_weekly: "At most weekly",
  notification_frequency_monthly: "At most monthly",
  notification_channel: "Alert delivery",
  notification_channel_help:
    "Where alert results reach you. In-app arrives immediately; email follows the frequency below.",
  notification_channel_help_saas:
    "Where alert results reach you. In-app needs the desktop app — in the browser, results just appear in the watchlist. Email follows the frequency below.",
  notification_channel_in_app_warning:
    "In-app notifications only arrive while the app is open. Without email, results that land while you're away wait silently in the watchlist.",
  notification_channel_in_app_saas_warning:
    "In-app notifications need the desktop app, not the browser. Add email to be notified here.",
  notification_settings_load_failed: "Unable to load notification settings",
  notification_settings_update_failed: "Unable to update notification settings",
  change_password: "Change password",
  change_password_help: "Use your current password to choose a new password for this account.",
  current_password: "Current password",
  new_password: "New password",
  change_password_saved: "Password updated.",
  change_password_failed: "Unable to change password",
  mfa: "Two-factor authentication",
  mfa_enabled: "Enabled",
  mfa_disabled: "Disabled",
  mfa_enabled_help: "Password login requires a code from your authenticator app.",
  mfa_disabled_help: "Add a second step to password login with an authenticator app.",
  mfa_enable: "Enable 2FA",
  mfa_disable: "Disable 2FA",
  mfa_enroll_help:
    "Add this secret to your authenticator app, then enter the generated 6-digit code.",
  mfa_disable_help: "Enter a current authenticator code to disable two-factor authentication.",
  mfa_code: "Authenticator code",
  mfa_qr_code: "Scan this QR code with your authenticator app",
  mfa_confirm: "Confirm code",
  mfa_copy_secret: "Copy secret",
  mfa_open_authenticator: "Open in authenticator app",
  mfa_load_failed: "Unable to load two-factor status",
  mfa_start_failed: "Unable to start two-factor enrollment",
  mfa_confirm_failed: "Unable to enable two-factor authentication",
  mfa_disable_failed: "Unable to disable two-factor authentication",
  theme: "Theme",
  theme_light: "Light",
  theme_dark: "Dark",
  toggle_on: "On",
  toggle_off: "Off",
  recording_review_before_upload: "Review before upload",
  recording_review_before_upload_help:
    "Keep each finished session on this computer until you approve or delete it. When off, sessions upload as soon as they finish.",
  recording_review_load_failed: "Unable to load review before upload setting",
  pending_review_title: "Sessions awaiting your review",
  pending_review_message:
    "{{count}} sessions are still waiting for your review. Upload them now, or keep them for review?",
  pending_review_message_one:
    "{{count}} session is still waiting for your review. Upload it now, or keep it for review?",
  pending_review_upload: "Upload now",
  pending_review_keep: "Keep for review",
  pending_review_upload_failed: "Uploading the sessions awaiting review failed",
} satisfies SettingsTranslation;
